import React, { useContext, useState } from 'react';
import { Link } from 'react-router';
import classnames from 'classnames';
import { useQuery } from '@apollo/client';
import { Wrapper, Container } from 'src/components/Layout';
import { RichText } from 'src/components/RichText/RichText';
import { CategoryQueryContext } from 'src/components/CategoryListingPage/CategoryQueryProvider';
import { LazyImg } from 'src/hooks/useLazyLoad';
import { useDeps } from 'src/hooks/useDeps';
import getCmsBlocks from 'src/queries/getCmsBlocks.graphql';
import { cmsBlocks, cmsBlocksVariables } from 'src/queries/__generated__/cmsBlocks';
import { getIndividualWidgets } from 'src/queries/__generated__/getIndividualWidgets';
import classes from 'src/components/IndividualPage/IndividualPage.scss';
import { Subcategory } from 'src/components/CategoryListingPage/CategoryQueryProvider';
import { DefaultMeta } from 'src/components/AppShell/Head';
import { useViewport } from 'src/hooks/useViewport';
import { CategoryFaq } from 'src/components/CategoryFaq/CategoryFaq';

import getWidgets from '../../../src/queries/getIndividualWidgets.graphql';
import { Carousel } from '../../elements/Carousel/Carousel';
import { TrustedBy } from '../../elements/Trusted/TrustedBy';
import { Button } from '../Button/Button';

import { FeaturedProducts } from './FeaturedProducts';

const DEFAULT_IMG_URL = 'https://staging-m2.workwearexpress.com/media/catalog/category/GD044_LS01_2020.jpg';

enum BannerSizes {
    Small = 'small',
    Big = 'big',
}

const defaultMediaLabel = 'Media Image';

export const IndividualPage: React.FC = () => {
    const [readMore, setReadMore] = useState(false);
    const { isMobile } = useViewport();
    const maxLengthOfDescription = isMobile ? 250 : 500;

    const {
        heroBannerDesktop,
        heroBannerDesktopSize,
        urlKey,
        subcategories,
        name,
        bestSellers,
        meta_title,
        meta_description,
        description,
        faq,
    } = useContext(CategoryQueryContext);

    const isLongDescription = description && description.length > maxLengthOfDescription;

    const {
        data: widgetsData,
        loading: widgetsLoading,
        error: widgetsError,
    } = useQuery<getIndividualWidgets>(getWidgets, { ssr: useDeps().env.SSR_GQL });

    const { data: cmsData } = useQuery<cmsBlocks, cmsBlocksVariables>(getCmsBlocks, {
        variables: { identifiers: [`${name.toLowerCase().split(' ').join('_')}_block`] },
        ssr: useDeps().env.SSR_GQL,
        skip: !name,
    });

    const item = widgetsData?.categoryFeaturedWidget.filter((item) => {
        if (item?.title === name) {
            return item;
        }
    });

    if (widgetsLoading || widgetsError || !widgetsData) {
        return null;
    }

    const meta = {
        title: meta_title || name || 'unknown',
        description: meta_description || name || 'unknown',
    };

    const { trustedByWidget, imageWidget } = widgetsData;

    const imageWidgetData = imageWidget.map((el) => ({
        image: el?.widget_parameters?.image,
    }));
    const trustedByWidgetData = trustedByWidget.map((el) => ({
        link: el?.widget_parameters?.link,
        image: el?.widget_parameters?.image,
    }));

    return (
        <>
            <DefaultMeta data={meta} withoutPrefix />
            <Wrapper bg="grey" variant="widget-wrapper">
                <Container>
                    {heroBannerDesktop && (
                        <Banner image={heroBannerDesktop} size={heroBannerDesktopSize} width={BannerSizes.Big} />
                    )}
                    {description && (
                        <>
                            <div
                                className={classnames(classes.description, {
                                    [classes.descriptionActive]: readMore,
                                    [classes.descriptionLong]: isLongDescription,
                                })}
                            >
                                <RichText className={classes.descriptionText} content={description} />
                                {isLongDescription && !readMore && <div className={classes.shade} />}
                            </div>
                            {isLongDescription && (
                                <Button
                                    className={classes.descriptionButton}
                                    onClick={() => setReadMore(!readMore)}
                                    variant="reset"
                                >
                                    {readMore ? 'Read Less' : 'Read More'}
                                </Button>
                            )}
                        </>
                    )}
                    {subcategories && <CatImg items={subcategories} categoryUrl={urlKey} />}
                    <FeaturedProducts
                        bestSellers={bestSellers}
                        skus={
                            (item &&
                                item[0] &&
                                item[0].widget_parameters &&
                                item[0].widget_parameters.sku &&
                                item[0].widget_parameters.sku.split(', ')) ||
                            []
                        }
                        listId={urlKey}
                        listName={name}
                        isSlider
                    />
                    {cmsData && <CmsBlock cmsData={cmsData} />}
                    {imageWidgetData && <Carousel items={imageWidgetData} mainTitle="Customer Examples" showZoom />}
                    {trustedByWidgetData && <TrustedBy items={trustedByWidgetData} />}
                    {faq?.length > 0 && <CategoryFaq title={name} faq={faq} />}
                </Container>
            </Wrapper>
        </>
    );
};

type BannerProps = {
    image: string;
    size?: { width: number; height: number } | null;
    width: string;
    verticalPosition?: string;
};

const Banner: React.FC<BannerProps> = ({ width, image, size, verticalPosition = 'center' }) => (
    <div className={classnames(classes.bannerWrapper, { [classes.bannerBig]: width === BannerSizes.Big })}>
        <div className={classes.bannerImgContainer}>
            <img
                src={image}
                alt={image}
                width={size?.width}
                height={size?.height}
                style={{ objectPosition: `center ${verticalPosition}` }}
            />
        </div>
    </div>
);

type CatImgProps = {
    items: (Subcategory | null)[];
    categoryUrl: string;
};

const CatImg: React.FC<CatImgProps> = ({ items, categoryUrl }) => {
    // No DOM here, so the server renders the same text as the browser and hydration keeps it.
    const truncateText = (html: string, maxLength: number): string => {
        if (!html) return '';

        const plainText = html.replace(/<[^>]*>/g, '').trim();

        if (plainText.length <= maxLength) {
            return html;
        }

        // Drop a half-cut entity such as `&am` so it doesn't render literally.
        return `${plainText
            .slice(0, maxLength)
            .replace(/&[^;\s]*$/, '')
            .trimEnd()}...`;
    };

    return (
        <div className={classes.catImg}>
            <ul className={classes.catImgList}>
                {items &&
                    items.map((item, i) => {
                        if (!item) return;
                        return (
                            <Link
                                to={`/${categoryUrl}/${item.url_key}` || '/'}
                                className={classes.catImgListWrapper}
                                key={i}
                            >
                                <li className={classes.catImgListElement}>
                                    <picture>
                                        <LazyImg
                                            src={item.image || DEFAULT_IMG_URL}
                                            alt={item.name || defaultMediaLabel}
                                            className={classes.catImgListElementImg}
                                        />
                                    </picture>
                                    <div className={classes.catImgListElementInfo}>
                                        <div>
                                            <h3 className={classes.catImgListElementInfoTitle}>{item.name}</h3>
                                            <div className={classes.catImgListElementInfoText}>
                                                <RichText content={item.short_description || ''} />
                                            </div>
                                            <div className={classes.catImgListElementInfoTextMobile}>
                                                <RichText content={truncateText(item.short_description || ``, 35)} />
                                            </div>
                                        </div>
                                        <Button element="span">Shop Now</Button>
                                    </div>
                                </li>
                            </Link>
                        );
                    })}
            </ul>
        </div>
    );
};

type CmsBlockProps = {
    cmsData: any;
};

const CmsBlock: React.FC<CmsBlockProps> = ({
    cmsData: {
        cmsBlocks: { items },
    },
}) => {
    return (
        <div className={classes.cmsBlockWrapper}>
            <RichText content={items[0].content} />
        </div>
    );
};
